import React, { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { useSearchParams } from 'react-router-dom'

import { useDataContext } from 'components/spreadsheet/contexts/data/useDataContext'
import { getRowLevelColour } from 'components/spreadsheet/helpers/colouring'
import { getRowValue } from 'components/spreadsheet/helpers/functions'

import EmptyState from './empty-state'
import { earliestEventStart, GetCellValue, GetRowColour, rowsToEvents } from './helpers'
import MonthGrid from './month-grid'
import Toolbar from './toolbar'

interface CalendarProps {
  setExpandModal: (expandModal: boolean, focusInput: boolean, rowId: string, rowNumber: number) => void
}

const Calendar: React.FC<CalendarProps> = ({ setExpandModal }) => {
  const { spreadsheetData, fitToHeight } = useDataContext()
  const [, setSearchParams] = useSearchParams()
  const settings = spreadsheetData.userConfiguration.calendarSettings ?? spreadsheetData.viewDetails.calendarSettings

  const [currentMonth, setCurrentMonth] = useState<DateTime>(DateTime.now().startOf('month'))
  const [hasUserNavigated, setHasUserNavigated] = useState(false)

  const openExpandedRow = (rowId: string) => {
    const bitmap = spreadsheetData.visibleBitmap
    let rowNumber = -1
    if (bitmap) {
      let visibleIdx = 0
      for (let i = 0; i < spreadsheetData.rows.length; i++) {
        if (bitmap[i]) {
          if (spreadsheetData.rows[i].publicId === rowId) {
            rowNumber = visibleIdx
            break
          }
          visibleIdx++
        }
      }
    } else {
      rowNumber = spreadsheetData.rows.findIndex((row) => row.publicId === rowId)
    }
    if (rowNumber === -1) return
    setSearchParams(
      (params) => {
        params.set('row', rowId)
        return params
      },
      { replace: true }
    )
    setExpandModal(true, false, rowId, rowNumber)
  }

  const bitmap = spreadsheetData.visibleBitmap
  const visibleRows = bitmap ? spreadsheetData.rows.filter((_, i) => bitmap[i]) : spreadsheetData.rows

  // Initial-month selection: on first load (before the user navigates), jump
  // the calendar to the month containing the earliest event. If the table is
  // empty or filters exclude everything, leave the view at today.
  useEffect(() => {
    if (hasUserNavigated) return
    if (spreadsheetData.loading) return
    if (!settings || !settings.labelColumnPublicId || !settings.startColumnPublicId) return

    const getCellForEffect: GetCellValue = (row, columnId) =>
      getRowValue(row, columnId, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)
    const eventsForEffect = rowsToEvents(
      visibleRows,
      settings.labelColumnPublicId,
      settings.startColumnPublicId,
      settings.endColumnPublicId,
      getCellForEffect
    )
    const earliest = earliestEventStart(eventsForEffect)
    if (earliest) {
      setCurrentMonth(earliest.startOf('month'))
      setHasUserNavigated(true) // latch: one-shot auto-jump, no re-jumps on subsequent row changes
    }
    // If no earliest (empty table / filters exclude all), leave at today.
  }, [
    spreadsheetData.loading,
    spreadsheetData.rows,
    spreadsheetData.visibleBitmap,
    spreadsheetData.columnIdToIndex,
    spreadsheetData.columnStore,
    settings,
    hasUserNavigated
  ])

  const goPrev = () => {
    setHasUserNavigated(true)
    setCurrentMonth((m) => m.minus({ months: 1 }))
  }
  const goNext = () => {
    setHasUserNavigated(true)
    setCurrentMonth((m) => m.plus({ months: 1 }))
  }
  const goToday = () => {
    setHasUserNavigated(true)
    setCurrentMonth(DateTime.now().startOf('month'))
  }

  if (!settings || !settings.startColumnPublicId || !settings.labelColumnPublicId) {
    return <EmptyState />
  }

  // Build cell-value getter closure matching Task 9's signature. The calendar
  // helper does not know about `columnIdToIndex` / the compressed column store,
  // so we build this closure once and pass it in.
  const getCell: GetCellValue = (row, columnId) =>
    getRowValue(row, columnId, spreadsheetData.columnIdToIndex, spreadsheetData.columnStore)

  // Resolve each row's pill colour from view colour rules.
  //
  // Multi-day pills (an end column is configured) only honour `applyToEntireRow`
  // rules — a multi-day bar spans many "cells", so a cell-level rule has no
  // unambiguous target.
  //
  // Single-day pills (no end column) *are* the start-date cell, so in addition
  // to `applyToEntireRow` rules they honour cell-level rules that target the
  // start column. A cell-level rule on any other column has no representation on
  // the pill, so it must not colour it.
  const colourSettings = spreadsheetData.userConfiguration.colourSettings
  const rulesForResolver = settings.endColumnPublicId
    ? colourSettings.filter((rule) => rule.applyToEntireRow)
    : colourSettings
        .filter((rule) => rule.applyToEntireRow || rule.columnId === settings.startColumnPublicId)
        .map((rule) => ({ ...rule, applyToEntireRow: true }))
  const columnLookup = new Map(spreadsheetData.viewDetails.columns.map((column) => [column.publicId, column]))
  const getRowColour: GetRowColour = (row) => {
    if (rulesForResolver.length === 0) return null
    const result = getRowLevelColour(
      rulesForResolver,
      row,
      columnLookup,
      spreadsheetData.columnIdToIndex,
      spreadsheetData.columnValuesCount,
      false,
      undefined,
      spreadsheetData.columnStore
    )
    if (result.matchedRuleIndex < 0) return null
    return {
      backgroundColour: result.backgroundColour,
      fontColour: result.fontColour,
      fontBold: result.fontBold,
      fontItalic: result.fontItalic,
      fontUnderline: result.fontUnderline
    }
  }

  const events = rowsToEvents(
    visibleRows,
    settings.labelColumnPublicId,
    settings.startColumnPublicId,
    settings.endColumnPublicId,
    getCell,
    getRowColour
  )

  return (
    <div className="w-full h-full flex flex-col bg-surface">
      <Toolbar currentMonth={currentMonth} onPrev={goPrev} onNext={goNext} onToday={goToday} />
      <MonthGrid currentMonth={currentMonth} events={events} onEventClick={openExpandedRow} fitToHeight={fitToHeight} />
    </div>
  )
}

export default Calendar
